import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { uploadAnswerFile } from '../../services/studentService';
import { extOf, fileColor } from '../Common/fileKind';

/* Номер с ручной проверкой: вместо поля «Ответ» ученик присылает решение
   целиком — текст, файлы и комментарий к ним. Проверяет человек, поэтому
   сразу после отправки номер уходит в состояние «на проверке», а когда
   куратор поставит баллы, здесь же появится его вердикт. */

const IcClip = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);
const IcX = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const sizeLabel = (b) => (!b ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} МБ` : `${Math.max(1, Math.round(b / 1024))} КБ`);

export default function ManualSolution({ homework, value, onChange, onSubmit, sending, readOnly }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const sent = homework?.answer;
  const files = value.files || [];

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (files.length >= 10) return toast.error('Больше десяти файлов не нужно');
    setUploading(true);
    try {
      const r = await uploadAnswerFile(homework.id, file);
      onChange({ ...value, files: [...files, r.data] });
      toast.success('Файл прикреплён');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Не удалось загрузить файл');
    } finally { setUploading(false); }
  };

  const drop = (i) => onChange({ ...value, files: files.filter((_, k) => k !== i) });

  // Вердикт проверяющего — показываем, как только работа проверена
  const verdict = sent && sent.status !== 'pending' && sent.reviewedAt ? {
    ok: sent.status === 'correct',
    score: sent.score,
    max: homework.maxScore ?? 1,
    comment: sent.reviewComment,
  } : null;

  return (
    <div className="ms-box">
      <div className="ms-head">
        <span className="ms-tag">Проверяет куратор</span>
        <span className="ms-hint">Ответ сверяется не автоматически — пришли решение, его посмотрит человек</span>
      </div>

      {verdict && (
        <div className={`ms-verdict${verdict.ok ? ' ok' : ' no'}`}>
          <div className="ms-verdict-top">
            <b>{verdict.ok ? 'Зачтено' : 'Не зачтено'}</b>
            {verdict.score != null && <span className="ms-score">{verdict.score} из {verdict.max}</span>}
          </div>
          {verdict.comment && <div className="ms-verdict-text">{verdict.comment}</div>}
        </div>
      )}
      {sent && sent.status === 'pending' && (
        <div className="ms-verdict wait">
          <b>Решение сохранено</b> — куратор посмотрит его, когда завершишь всё ДЗ. Ответ придёт уведомлением
        </div>
      )}

      <label className="ms-label">Решение</label>
      <textarea className="ms-area" rows={6} disabled={readOnly}
        placeholder="Ход решения, формулы, код или просто файлик — всё, что хочешь показать"
        value={value.answer || ''} onChange={e => onChange({ ...value, answer: e.target.value })} />

      <label className="ms-label">Файлы</label>
      <div className="ms-files">
        {files.map((f, i) => (
          <div key={i} className="ms-file">
            <span className="ms-file-ic" style={{ background: fileColor(f.name)[0], color: fileColor(f.name)[1] }}>{extOf(f.name) || 'файл'}</span>
            <a href={f.url} target="_blank" rel="noopener noreferrer" className="ms-file-name">{f.name}</a>
            <span className="ms-file-size">{sizeLabel(f.size)}</span>
            {!readOnly && <button type="button" className="ms-file-x" onClick={() => drop(i)} aria-label="Убрать файл"><IcX /></button>}
          </div>
        ))}
        {!readOnly && (
          <button type="button" className="ms-attach" onClick={() => fileRef.current?.click()} disabled={uploading}>
            <IcClip />{uploading ? 'Загружаем…' : 'Прикрепить файл'}
          </button>
        )}
        <input ref={fileRef} type="file" onChange={pick} style={{ display: 'none' }} />
      </div>

      {!readOnly && (
        <>
          <button type="button" className="ms-send" onClick={onSubmit} disabled={sending}>
            {sending ? 'Отправляем…' : sent ? 'Отправить заново' : 'Отправить на проверку'}
          </button>
          {/* Работа уходит куратору целиком при завершении ДЗ — пишем об этом
              прямо, иначе ученик ждёт проверки сразу после кнопки */}
          {verdict
            ? <div className="ms-warn">Если отправишь заново, баллы и комментарий куратора сбросятся — работу проверят ещё раз.</div>
            : <div className="ms-warn">Задание уйдёт на проверку, когда завершишь всё ДЗ — до этого решение можно менять.</div>}
        </>
      )}
    </div>
  );
}
